import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { 
  Package, Scan, Plus, Minus, AlertTriangle, Check, 
  Search, Filter, RefreshCw, ShoppingCart, Truck, 
  BarChart3, Camera, X, Loader2, QrCode, Box, 
  ClipboardList, TrendingDown, Archive
} from 'lucide-react';
import { toast } from 'sonner';

export default function SupplyManagement() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [supplies, setSupplies] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanQuantity, setScanQuantity] = useState(-1);
  const [processing, setProcessing] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  
  // New supply form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSupply, setNewSupply] = useState({
    name: '', category: 'Medical', unit_of_measure: 'each',
    reorder_threshold: 10, reorder_quantity: 25, auto_reorder_enabled: false
  });

  useEffect(() => {
    loadDashboard();
    loadCategories();
  }, []);

  useEffect(() => {
    if (activeTab === 'inventory') loadInventory();
    if (activeTab === 'orders') loadOrders();
    if (activeTab === 'history') loadTransactions();
    if (activeTab === 'catalog') loadSupplies();
  }, [activeTab]);

  const loadDashboard = async () => {
    try {
      const response = await api.get('/supplies/inventory/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setDashboard({ setup_required: true });
    } finally {
      setLoading(false);
    }
  };

  const loadSupplies = async () => {
    try {
      const response = await api.get('/supplies');
      setSupplies(response.data.supplies || []);
    } catch (error) {
      console.error('Error loading supplies:', error);
    }
  };

  const loadInventory = async () => {
    try {
      const response = await api.get('/supplies/inventory/all');
      setInventory(response.data.inventory || []);
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const loadOrders = async () => {
    try {
      const response = await api.get('/supplies/orders');
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await api.get('/supplies/transactions?limit=100');
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.get('/supplies/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      setCategories(['Medical', 'Hygiene', 'Cleaning', 'PPE', 'Office', 'Other']);
    }
  };

  const handleScan = async (barcode) => {
    if (!barcode) return;
    setProcessing(true);
    
    try {
      const response = await api.get(`/supplies/scan/${barcode}`);
      setScanResult(response.data);
      setScanQuantity(-1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Supply not found');
      setScanResult(null);
    } finally {
      setProcessing(false);
    }
  };

  const processScanTransaction = async () => {
    if (!scanResult || scanQuantity === 0) return;
    setProcessing(true);
    
    try {
      const response = await api.post('/supplies/scan', {
        barcode: scanResult.id,
        quantity: scanQuantity,
        scan_method: 'manual'
      });
      
      if (response.data.success) {
        toast.success(
          `${scanQuantity > 0 ? 'Added' : 'Removed'} ${Math.abs(scanQuantity)} ${scanResult.name}`
        );
        setScanResult({ ...scanResult, quantity_on_hand: response.data.transaction.new_qty });
        
        if (response.data.low_stock_warning) {
          toast.warning(`Low stock alert: ${scanResult.name}`);
        }
        
        // Refresh data
        loadDashboard();
        if (activeTab === 'inventory') loadInventory();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process');
    } finally {
      setProcessing(false);
    }
  };

  const createSupply = async () => {
    if (!newSupply.name) {
      toast.error('Name is required');
      return;
    }
    
    try {
      await api.post('/supplies', newSupply);
      toast.success('Supply created');
      setShowAddForm(false);
      setNewSupply({
        name: '', category: 'Medical', unit_of_measure: 'each',
        reorder_threshold: 10, reorder_quantity: 25, auto_reorder_enabled: false
      });
      loadSupplies();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create supply');
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/supplies/orders/${orderId}/status?status=${status}`);
      toast.success(`Order ${status}`);
      loadOrders();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'scanner', label: 'Scanner', icon: Scan },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'orders', label: 'Orders', icon: ShoppingCart },
    { id: 'catalog', label: 'Catalog', icon: Archive },
    { id: 'history', label: 'History', icon: ClipboardList }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200 pb-2 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'bg-emerald-100 text-emerald-700 font-medium' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`supply-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Setup Required Banner */}
      {dashboard?.setup_required && (
        <Card className="p-6 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-amber-800">Database Setup Required</h3>
              <p className="text-sm text-amber-700 mt-1">
                Please run the SQL script from <code className="bg-amber-100 px-1 rounded">/app/backend/sql/supply_management_schema.sql</code> in your Supabase SQL Editor.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && !dashboard.setup_required && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="p-4 bg-gradient-to-br from-emerald-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Supplies</p>
                  <p className="text-2xl font-bold text-emerald-700">{dashboard.total_supplies}</p>
                </div>
                <Package className="w-8 h-8 text-emerald-300" />
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-blue-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Items in Stock</p>
                  <p className="text-2xl font-bold text-blue-700">{dashboard.total_items_in_stock}</p>
                </div>
                <Box className="w-8 h-8 text-blue-300" />
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-amber-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Low Stock</p>
                  <p className="text-2xl font-bold text-amber-700">{dashboard.low_stock_count}</p>
                </div>
                <TrendingDown className="w-8 h-8 text-amber-300" />
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-red-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Out of Stock</p>
                  <p className="text-2xl font-bold text-red-700">{dashboard.out_of_stock_count}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-300" />
              </div>
            </Card>

            <Card className="p-4 bg-gradient-to-br from-purple-50 to-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Orders</p>
                  <p className="text-2xl font-bold text-purple-700">{dashboard.pending_orders}</p>
                </div>
                <Truck className="w-8 h-8 text-purple-300" />
              </div>
            </Card>
          </div>

          {/* Alerts */}
          {(dashboard.low_stock_items?.length > 0 || dashboard.out_of_stock_items?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-6">
              {dashboard.low_stock_items?.length > 0 && (
                <Card className="p-4 border-amber-200">
                  <h3 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5" />
                    Low Stock Alerts
                  </h3>
                  <div className="space-y-2">
                    {dashboard.low_stock_items.map(item => (
                      <div key={item.id} className="flex justify-between items-center p-2 bg-amber-50 rounded">
                        <span className="font-medium">{item.name}</span>
                        <span className="text-amber-700 font-bold">{item.quantity} left</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {dashboard.out_of_stock_items?.length > 0 && (
                <Card className="p-4 border-red-200">
                  <h3 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Out of Stock
                  </h3>
                  <div className="space-y-2">
                    {dashboard.out_of_stock_items.map(item => (
                      <div key={item.id} className="flex justify-between items-center p-2 bg-red-50 rounded">
                        <span className="font-medium">{item.name}</span>
                        <span className="text-red-700 font-bold">0</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Quick Actions */}
          <Card className="p-6">
            <h3 className="font-semibold mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => setActiveTab('scanner')} className="bg-emerald-600 hover:bg-emerald-700">
                <Scan className="w-4 h-4 mr-2" />
                Scan Supply
              </Button>
              <Button variant="outline" onClick={() => { setActiveTab('catalog'); setShowAddForm(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add New Supply
              </Button>
              <Button variant="outline" onClick={() => setActiveTab('inventory')}>
                <ClipboardList className="w-4 h-4 mr-2" />
                Take Inventory Count
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Scanner Tab */}
      {activeTab === 'scanner' && (
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Scan className="w-5 h-5 text-emerald-600" />
              Supply Scanner
            </h3>
            
            {/* Manual Entry */}
            <div className="flex gap-4 mb-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enter Barcode, SKU, or Supply ID
                </label>
                <input
                  type="text"
                  value={manualBarcode}
                  onChange={(e) => setManualBarcode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleScan(manualBarcode)}
                  placeholder="Scan or type barcode..."
                  className="w-full border rounded-lg px-3 py-2"
                  data-testid="supply-barcode-input"
                />
              </div>
              <Button 
                onClick={() => handleScan(manualBarcode)}
                disabled={processing || !manualBarcode}
                className="self-end bg-emerald-600 hover:bg-emerald-700"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {/* Scan Result */}
            {scanResult && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-lg font-semibold">{scanResult.name}</h4>
                    <p className="text-sm text-gray-500">
                      SKU: {scanResult.sku} | Category: {scanResult.category}
                    </p>
                  </div>
                  <button onClick={() => setScanResult(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Current Quantity */}
                <div className="text-center py-4 mb-4 bg-white rounded-lg">
                  <p className="text-sm text-gray-500">Current Quantity</p>
                  <p className={`text-4xl font-bold ${
                    scanResult.is_low_stock ? 'text-amber-600' : 'text-emerald-600'
                  }`}>
                    {scanResult.quantity_on_hand}
                  </p>
                  <p className="text-sm text-gray-400">{scanResult.unit_of_measure}</p>
                  {scanResult.is_low_stock && (
                    <p className="text-amber-600 text-sm mt-2 flex items-center justify-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      Low stock - reorder at {scanResult.reorder_threshold}
                    </p>
                  )}
                </div>

                {/* Quick Quantity Buttons */}
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Quick Remove</p>
                  <div className="flex gap-2 flex-wrap">
                    {[-1, -2, -5, -10].map(qty => (
                      <Button
                        key={qty}
                        variant={scanQuantity === qty ? 'default' : 'outline'}
                        onClick={() => setScanQuantity(qty)}
                        className={scanQuantity === qty ? 'bg-red-600' : 'border-red-200 text-red-600'}
                      >
                        <Minus className="w-4 h-4 mr-1" />
                        {Math.abs(qty)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Quick Add</p>
                  <div className="flex gap-2 flex-wrap">
                    {[1, 5, 10, 25].map(qty => (
                      <Button
                        key={qty}
                        variant={scanQuantity === qty ? 'default' : 'outline'}
                        onClick={() => setScanQuantity(qty)}
                        className={scanQuantity === qty ? 'bg-emerald-600' : 'border-emerald-200 text-emerald-600'}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {qty}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Custom Quantity */}
                <div className="flex gap-4 items-end mb-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Quantity</label>
                    <input
                      type="number"
                      value={scanQuantity}
                      onChange={(e) => setScanQuantity(parseInt(e.target.value) || 0)}
                      className="w-full border rounded-lg px-3 py-2"
                      placeholder="Enter quantity (negative to remove)"
                    />
                  </div>
                </div>

                {/* Submit */}
                <Button
                  onClick={processScanTransaction}
                  disabled={processing || scanQuantity === 0}
                  className={`w-full ${scanQuantity > 0 ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
                  data-testid="process-scan-btn"
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : scanQuantity > 0 ? (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Add {scanQuantity} to Inventory
                    </>
                  ) : (
                    <>
                      <Minus className="w-4 h-4 mr-2" />
                      Remove {Math.abs(scanQuantity)} from Inventory
                    </>
                  )}
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Full Inventory</h3>
            <Button variant="outline" onClick={loadInventory}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {inventory.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No supplies in inventory yet.</p>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Supply</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Category</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">On Hand</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Reorder At</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {inventory.map(item => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium">{item.name}</p>
                        <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{item.category}</td>
                      <td className="px-4 py-3 text-right font-bold">{item.quantity_on_hand}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{item.reorder_threshold}</td>
                      <td className="px-4 py-3 text-center">
                        {item.quantity_on_hand === 0 ? (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">Out of Stock</span>
                        ) : item.is_low_stock ? (
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs">Low Stock</span>
                        ) : (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">In Stock</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Supply Orders</h3>
            <Button variant="outline" onClick={loadOrders}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {orders.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No orders yet. Auto-reorders will appear here when supplies run low.</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <Card key={order.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{order.order_number}</p>
                      <p className="text-sm text-gray-500">
                        {order.triggered_by === 'auto' ? '🤖 Auto-generated' : '👤 Manual'} | 
                        {order.vendor_name || 'No vendor'}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        order.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                        order.status === 'ordered' ? 'bg-purple-100 text-purple-700' :
                        order.status === 'shipped' ? 'bg-indigo-100 text-indigo-700' :
                        order.status === 'received' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {order.status}
                      </span>
                      
                      {order.status === 'pending' && (
                        <Button size="sm" onClick={() => updateOrderStatus(order.id, 'approved')}>
                          Approve
                        </Button>
                      )}
                      {order.status === 'approved' && (
                        <Button size="sm" onClick={() => updateOrderStatus(order.id, 'ordered')}>
                          Mark Ordered
                        </Button>
                      )}
                      {order.status === 'shipped' && (
                        <Button size="sm" className="bg-green-600" onClick={() => updateOrderStatus(order.id, 'received')}>
                          Receive
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Catalog Tab */}
      {activeTab === 'catalog' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Supply Catalog</h3>
            <Button onClick={() => setShowAddForm(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Supply
            </Button>
          </div>

          {/* Add Form */}
          {showAddForm && (
            <Card className="p-6">
              <h4 className="font-semibold mb-4">New Supply</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={newSupply.name}
                    onChange={(e) => setNewSupply({ ...newSupply, name: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Supply name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newSupply.category}
                    onChange={(e) => setNewSupply({ ...newSupply, category: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure</label>
                  <select
                    value={newSupply.unit_of_measure}
                    onChange={(e) => setNewSupply({ ...newSupply, unit_of_measure: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="each">Each</option>
                    <option value="box">Box</option>
                    <option value="case">Case</option>
                    <option value="roll">Roll</option>
                    <option value="pack">Pack</option>
                    <option value="bottle">Bottle</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Threshold</label>
                  <input
                    type="number"
                    value={newSupply.reorder_threshold}
                    onChange={(e) => setNewSupply({ ...newSupply, reorder_threshold: parseInt(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="auto-reorder"
                    checked={newSupply.auto_reorder_enabled}
                    onChange={(e) => setNewSupply({ ...newSupply, auto_reorder_enabled: e.target.checked })}
                  />
                  <label htmlFor="auto-reorder" className="text-sm">Enable auto-reorder when low</label>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={createSupply} className="bg-emerald-600">
                  <Check className="w-4 h-4 mr-2" />
                  Create Supply
                </Button>
                <Button variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
              </div>
            </Card>
          )}

          {/* Supplies List */}
          {supplies.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              <Archive className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No supplies in catalog. Add your first supply above.</p>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {supplies.map(supply => (
                <Card key={supply.id} className="p-4">
                  <h4 className="font-medium">{supply.name}</h4>
                  <p className="text-sm text-gray-500 mb-2">
                    SKU: {supply.sku} | {supply.category}
                  </p>
                  <div className="flex justify-between text-sm">
                    <span>Reorder at: {supply.reorder_threshold}</span>
                    {supply.auto_reorder_enabled && (
                      <span className="text-emerald-600">🔄 Auto-reorder</span>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Transaction History</h3>
            <Button variant="outline" onClick={loadTransactions}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {transactions.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No transactions yet.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {transactions.map(trans => (
                <Card key={trans.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        trans.transaction_type === 'add' ? 'bg-emerald-100 text-emerald-600' :
                        trans.transaction_type === 'remove' ? 'bg-red-100 text-red-600' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        {trans.transaction_type === 'add' ? <Plus className="w-4 h-4" /> :
                         trans.transaction_type === 'remove' ? <Minus className="w-4 h-4" /> :
                         <RefreshCw className="w-4 h-4" />}
                      </div>
                      <div>
                        <p className="font-medium">{trans.supplies?.name || 'Unknown Supply'}</p>
                        <p className="text-xs text-gray-500">
                          {trans.employee_name} • {new Date(trans.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${trans.quantity > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {trans.quantity > 0 ? '+' : ''}{trans.quantity}
                      </p>
                      <p className="text-xs text-gray-500">
                        {trans.quantity_before} → {trans.quantity_after}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
